import { useEffect } from 'react';

/**
 * Holds a screen wake lock while `active`, so the phone doesn't auto-lock mid-ride — a
 * glanceable riding app is useless behind a lock screen, and `watchPosition` (thus
 * recording) dies with it. The OS releases wake locks whenever the page is hidden
 * (manual lock button, app switch), so we re-acquire on every return to visibility.
 * Best-effort: unsupported browsers (iOS < 16.4) and low-battery refusals are ignored.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;

    let lock: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (cancelled) {
          void sentinel.release().catch(() => {});
        } else {
          lock = sentinel;
        }
      } catch {
        // Denied (battery saver) or unsupported — nothing to do, the screen will
        // behave as it always did.
      }
    };

    void acquire();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void lock?.release().catch(() => {});
    };
  }, [active]);
}
